/*
 * Procedural geometry for the startup-loader blueprint: a meridional (half)
 * section of a two-spool high-bypass turbofan in the CFM LEAP-1B class
 * (Icelandair's 737 MAX engine). Pure and deterministic - `ENGINE` is built
 * once at module load and the component only renders the resulting strings.
 *
 * Coordinates: authored as (x, r) = (axial station, radius above the centre
 * line) in drawing units and converted to SVG space by `pt()`. Only the TOP
 * half is generated; the component mirrors it about the axis with <use>.
 * Scale: fan tip radius 190 units = 0.88 m, i.e. ~216 units per metre.
 *
 * Stage counts are the constants below. For a LEAP-1A (A320neo family) set
 * LPT_STAGES to 7 - the LPT annulus is spread over whatever count is given.
 * Blade and stage counts should be confirmed with an engine SME.
 */

export const VIEW_W = 1200;
export const VIEW_H = 640;
export const AXIS_Y = 320;

export const FAN_BLADES = 18;
export const BOOSTER_STAGES = 3;
export const HPC_STAGES = 10;
export const HPT_STAGES = 2;
export const LPT_STAGES = 5;

/** Explode offset per module index, in drawing units. Must equal
 *  `--ds-splash-gap` (a % of the 1200-unit drawing width) in tokens.css. */
export const GAP_UNITS = 24;
/** Index of the module that stays put when the engine explodes. */
export const EXPLODE_CENTRE = 3.5;

const UNITS_PER_M = 380 / 1.76;
/** Shifts the whole engine left so the exploded view still fits the sheet. */
const X_SHIFT = -22;

export type Ink = 'hatch' | 'fill' | 'ghost' | 'line' | 'muted' | 'flame' | 'solid';
const INK_ORDER: Ink[] = ['hatch', 'fill', 'ghost', 'line', 'muted', 'flame', 'solid'];

export interface Part {
  ink: Ink;
  d: string;
}

export interface Callout {
  n: number;
  name: string;
  /** Leader anchor, SVG coordinates (assembled position). */
  ax: number;
  ay: number;
  /** Balloon centre, SVG coordinates. */
  bx: number;
  by: number;
}

export interface EngineModule {
  id: string;
  /** Axial explode index; offset = (i - EXPLODE_CENTRE) * GAP_UNITS. */
  i: number;
  /** Radial lift when exploded (drawing units, away from the axis). */
  dy: number;
  /** Draw-in order (stagger slot). */
  order: number;
  /** Mirrored about the axis. */
  parts: Part[];
  /** Drawn in the top half only (e.g. the radial drive shaft). */
  topOnly: Part[];
  callout?: Callout;
}

export interface Dimension {
  lines: Part[];
  labels: { x: number; y: number; text: string; rotate?: number }[];
}

export interface Engine {
  modules: EngineModule[];
  shafts: Part[];
  /** Plain shaft outlines, drawn dashed while the engine is exploded. */
  shaftOutline: string;
  dims: Dimension;
}

/* ── primitives ───────────────────────────────────────────────────────── */

const n = (v: number) => Math.round(v * 10) / 10;
const sx = (x: number) => n(x + X_SHIFT);
const sy = (r: number) => n(AXIS_Y - r);
const pt = (x: number, r: number) => `${sx(x)} ${sy(r)}`;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

type P = [number, number];

const poly = (pts: P[]) => `M${pts.map(([x, r]) => pt(x, r)).join('L')}Z`;
const line = (pts: P[]) => `M${pts.map(([x, r]) => pt(x, r)).join('L')}`;
const rect = (x0: number, x1: number, r0: number, r1: number) =>
  poly([[x0, r0], [x1, r0], [x1, r1], [x0, r1]]);
const circle = (x: number, r: number, rad: number) =>
  `M${n(sx(x) - rad)} ${sy(r)}a${rad} ${rad} 0 1 0 ${2 * rad} 0a${rad} ${rad} 0 1 0 ${-2 * rad} 0Z`;

/** Collects path data per ink so each module renders as a handful of paths. */
class Sketch {
  private buf: Record<Ink, string[]> = {
    hatch: [], fill: [], ghost: [], line: [], muted: [], flame: [], solid: [],
  };

  add(ink: Ink, d: string) {
    this.buf[ink].push(d);
    return this;
  }

  parts(): Part[] {
    return INK_ORDER.filter(k => this.buf[k].length).map(k => ({ ink: k, d: this.buf[k].join('') }));
  }
}

interface AirfoilSpec {
  x: number;
  rIn: number;
  rOut: number;
  chordIn: number;
  chordOut?: number;
  /** Axial shift of the leading edge from rIn to rOut (sweep / lean). */
  lean?: number;
  /** How far the leading edge bows forward at mid-span. */
  bow?: number;
}

/** Meridional outline of a blade or vane: curved leading/trailing edges. */
function airfoil({ x, rIn, rOut, chordIn, chordOut = chordIn, lean = 0, bow = 1.5 }: AirfoilSpec) {
  const leI: P = [x, rIn];
  const leO: P = [x + lean, rOut];
  const teO: P = [x + lean + chordOut, rOut];
  const teI: P = [x + chordIn, rIn];
  const rm = (rIn + rOut) / 2;
  const leM = pt((leI[0] + leO[0]) / 2 - bow, rm);
  const teM = pt((teI[0] + teO[0]) / 2 - bow * 0.6, rm);
  return `M${pt(...leI)}Q${leM} ${pt(...leO)}L${pt(...teO)}Q${teM} ${pt(...teI)}Z`;
}

/**
 * A blade row as it reads in a sectioned drawing: the sectioned blade plus
 * 1-3 fading "ghost" blades behind it (the neighbouring blades seen at an
 * angle, so progressively narrower in projection).
 */
function bladeRow(sk: Sketch, spec: AirfoilSpec, ghosts = 1) {
  sk.add('fill', airfoil(spec));
  for (let g = 1; g <= ghosts; g++) {
    const k = 1 - 0.28 * g;
    const chordOut = spec.chordOut ?? spec.chordIn;
    sk.add('ghost', airfoil({
      ...spec,
      x: spec.x + spec.chordIn * (1 - k) * 0.55,
      chordIn: spec.chordIn * k,
      chordOut: chordOut * k,
      rOut: spec.rOut - g * 0.8,
    }));
  }
}

interface DiscSpec {
  x: number;
  w: number;
  rRim: number;
  rBore: number;
  rimH?: number;
  web?: number;
  bore?: number;
  boreH?: number;
}

/** Rim / web / bore disc cross-section (the classic "dumbbell"). */
function disc({ x, w, rRim, rBore, rimH = 4, web = 0.3, bore = 0.8, boreH }: DiscSpec) {
  const c = x + w / 2;
  const ww = (w * web) / 2;
  const bw = (w * bore) / 2;
  const bh = boreH ?? Math.max(3, (rRim - rBore) * 0.22);
  const rw = rRim - rimH;
  return poly([
    [x, rRim], [x + w, rRim], [x + w, rw], [c + ww, rw], [c + ww, rBore + bh],
    [c + bw, rBore + bh], [c + bw, rBore], [c - bw, rBore], [c - bw, rBore + bh],
    [c - ww, rBore + bh], [c - ww, rw], [x, rw],
  ]);
}

/** Bearing symbol: box with crossed diagonals. */
function bearing(sk: Sketch, x0: number, x1: number, r0: number, r1: number) {
  sk.add('fill', rect(x0, x1, r0, r1));
  sk.add('line', line([[x0, r0], [x1, r1]]) + line([[x0, r1], [x1, r0]]));
}

/** Case wall that follows a (linear) flow-path line, `t` thick. */
function wall(x0: number, x1: number, r0: number, r1: number, t: number) {
  return poly([[x0, r0], [x1, r1], [x1, r1 + t], [x0, r0 + t]]);
}

/* ── modules ──────────────────────────────────────────────────────────── */

function inlet() {
  const sk = new Sketch();
  // Inlet cowl: lip highlight, throat, fan face on the inside; smooth outer.
  sk.add('fill',
    `M${pt(270, 193)}L${pt(205, 194)}Q${pt(175, 195)} ${pt(172, 205)}` +
    `Q${pt(174, 219)} ${pt(202, 223)}L${pt(270, 226)}Z`);
  sk.add('hatch', rect(266, 272, 193, 226)); // attach flange
  sk.add('muted', line([[205, 197.5], [264, 196.5]])); // acoustic liner
  sk.add('muted', line([[240, 197], [240, 224]])); // forward bulkhead
  sk.add('line', circle(186, 210, 2.6)); // anti-ice duct
  return sk;
}

function fan() {
  const sk = new Sketch();
  // Spinner.
  sk.add('fill', `M${pt(236, 0)}C${pt(240, 28)} ${pt(270, 54)} ${pt(300, 58)}L${pt(300, 0)}Z`);
  sk.add('muted', line([[272, 0], [272, 46]]));
  // Fan blade (swept, wide-chord) + platform + dovetail root.
  bladeRow(sk, { x: 298, rIn: 60, rOut: 188.5, chordIn: 50, chordOut: 62, lean: -15, bow: 7 }, 3);
  sk.add('fill', rect(296, 351, 57, 60));
  sk.add('fill', poly([[305, 57], [343, 57], [339, 51], [345, 45], [303, 45], [309, 51]]));
  sk.add('hatch', disc({ x: 300, w: 46, rRim: 45, rBore: 20, rimH: 6, web: 0.22, bore: 0.72 }));
  // Fan case: abradable liner, containment wall, flanges, aft case.
  sk.add('fill', rect(268, 392, 191, 195));
  sk.add('hatch', poly([
    [266, 195], [394, 195], [394, 201], [360, 202], [354, 214], [292, 214], [286, 202], [266, 201],
  ]));
  sk.add('hatch', rect(264, 270, 195, 219));
  sk.add('hatch', rect(390, 396, 195, 213));
  sk.add('hatch', rect(396, 448, 192, 197));
  sk.add('muted', line([[292, 208], [354, 208]]));
  // Outlet guide vanes (fan frame) in the bypass duct.
  bladeRow(sk, { x: 408, rIn: 115, rOut: 191.5, chordIn: 18, chordOut: 22, lean: 10, bow: 2 }, 1);
  return sk;
}

const bHub = (x: number) => lerp(70, 76, (x - 384) / 86);
const bTip = (x: number) => lerp(104, 97, (x - 384) / 86);

function booster() {
  const sk = new Sketch();
  const top = new Sketch();
  // Splitter nose + booster case.
  sk.add('hatch',
    `M${pt(376, 106)}Q${pt(382, 112)} ${pt(400, 114)}L${pt(472, 120)}` +
    `L${pt(472, bTip(472))}L${pt(384, bTip(384))}Q${pt(378, 104)} ${pt(376, 106)}Z`);
  for (let k = 0; k < BOOSTER_STAGES; k++) {
    const xr = 384 + k * 28;
    const cr = 12 - k;
    const xs = xr + 16;
    bladeRow(sk, { x: xr, rIn: bHub(xr), rOut: bTip(xr) - 1.2, chordIn: cr, lean: 2, bow: 2 }, 1);
    bladeRow(sk, { x: xs, rIn: bHub(xs) + 1.2, rOut: bTip(xs), chordIn: 9, lean: 1.5, bow: 1.5 }, 0);
    sk.add('line', line([[xs + 2, bHub(xs) - 1], [xs + 2, bHub(xs) + 1.2]]) +
      line([[xs + 6, bHub(xs) - 1], [xs + 6, bHub(xs) + 1.2]])); // seal teeth
  }
  // LP booster drum and its cone back to the fan disc.
  sk.add('hatch', poly([[382, bHub(382) - 1], [454, bHub(454) - 1], [454, bHub(454) - 6], [382, bHub(382) - 6]]));
  sk.add('hatch', poly([[342, 45], [386, bHub(386) - 1], [386, bHub(386) - 6], [342, 39]]));
  // Transition duct (gooseneck) into the HPC, with a fan hub frame strut.
  sk.add('hatch',
    `M${pt(472, 97)}C${pt(496, 97)} ${pt(506, 83)} ${pt(528, 83)}L${pt(528, 89)}` +
    `C${pt(506, 89)} ${pt(496, 103)} ${pt(472, 103)}Z`);
  sk.add('hatch',
    `M${pt(472, 76)}C${pt(496, 76)} ${pt(506, 54)} ${pt(528, 54)}L${pt(528, 48)}` +
    `C${pt(506, 48)} ${pt(496, 70)} ${pt(472, 70)}Z`);
  sk.add('fill', poly([[480, 75.5], [483, 96.5], [506, 86], [503, 60]]));
  sk.add('muted', line([[474, 101], [484, 110]])); // variable bleed valve door
  // Radial drive shaft + transfer gearbox (one position only).
  top.add('muted', line([[505, 46], [488, 116]]) + line([[511, 46], [494, 116]]));
  top.add('fill', rect(480, 504, 116, 125));
  top.add('fill', rect(502, 514, 38, 46));
  return { sk, top };
}

const cHub = (x: number) => lerp(54, 64, (x - 526) / 182);
const cTip = (x: number) => lerp(83, 70, (x - 526) / 182);

function hpc() {
  const sk = new Sketch();
  // Inlet guide vane.
  bladeRow(sk, { x: 528, rIn: cHub(528) + 1, rOut: cTip(528), chordIn: 5.5, bow: 1 }, 0);
  const pitch = (700 - 537) / (HPC_STAGES - 1);
  const discs: { x: number; w: number }[] = [];
  for (let k = 0; k < HPC_STAGES; k++) {
    const xr = 537 + k * pitch;
    const cr = 8 - 0.25 * k;
    const xs = xr + cr + 1.8;
    const cs = 6 - 0.15 * k;
    bladeRow(sk, { x: xr, rIn: cHub(xr), rOut: cTip(xr) - 1, chordIn: cr, lean: 1, bow: 1.2 }, 1);
    bladeRow(sk, { x: xs, rIn: cHub(xs) + 1, rOut: cTip(xs), chordIn: cs, lean: 0.5, bow: 1 }, 0);
    const h = cHub(xs);
    sk.add('line', line([[xs + 1.2, h - 3], [xs + 1.2, h + 0.6]]) + line([[xs + cs - 1.2, h - 3], [xs + cs - 1.2, h + 0.6]]));
    // Variable stator vanes on the IGV + first 3 stators: spindle, lever, ring.
    if (k < 3) {
      const cx = xs + cs / 2;
      const t = cTip(cx) + 5;
      sk.add('line', line([[cx, t], [cx, t + 7], [cx + 5, t + 9]]));
      sk.add('solid', circle(cx + 5, t + 9, 1.2));
    }
    discs.push({ x: xr - 0.5, w: cr + 1 });
  }
  discs.forEach((d, k) => {
    sk.add('hatch', disc({ x: d.x, w: d.w, rRim: cHub(d.x) - 0.3, rBore: 38, rimH: 3.5, web: 0.38, bore: 0.95, boreH: 3 }));
    const nx = discs[k + 1];
    if (nx) {
      const h = cHub((d.x + nx.x) / 2);
      sk.add('hatch', rect(d.x + d.w, nx.x, h - 4.5, h - 3));
    }
  });
  // Forward cone onto the HP shaft.
  sk.add('hatch', poly([[534, 41], [538, 41], [524, 34], [519, 34]]));
  // Case + flanges.
  sk.add('hatch', wall(524, 710, cTip(524), cTip(710), 5));
  [524, 600, 708].forEach(x => sk.add('hatch', rect(x - 2, x + 2, cTip(x) + 5, cTip(x) + 10)));
  return sk;
}

function combustor() {
  const sk = new Sketch();
  // Diffuser / combustor outer case and inner case.
  sk.add('hatch', poly([[708, 76], [714, 76], [730, 98], [794, 103], [794, 108], [728, 103], [708, 82]]));
  sk.add('hatch', rect(792, 796, 103, 113));
  sk.add('hatch', poly([[708, 58], [722, 56], [794, 58], [794, 53], [722, 51], [708, 53]]));
  sk.add('line', line([[708, 70], [722, 74]]) + line([[708, 64], [722, 60]])); // pre-diffuser
  // Annular liner: dome with swirler, outer and inner liner walls, cowls.
  sk.add('fill', poly([[728, 93], [790, 88], [790, 86.5], [728, 91.5]]));
  sk.add('fill', poly([[728, 65], [790, 67.5], [790, 69], [728, 66.5]]));
  sk.add('fill', rect(726, 729, 66.5, 75) + rect(726, 729, 83, 91.5));
  sk.add('fill', rect(723, 730, 75, 83));
  sk.add('line',
    `M${pt(726, 91.5)}Q${pt(718, 92)} ${pt(716, 87)}` +
    `M${pt(726, 66.5)}Q${pt(718, 66)} ${pt(716, 71)}`);
  // Dilution holes.
  [748, 766].forEach(x => {
    sk.add('muted', circle(x, lerp(92.3, 88, (x - 728) / 62) - 0.3, 1.4));
    sk.add('muted', circle(x, lerp(65.8, 68.2, (x - 728) / 62), 1.4));
  });
  // Fuel nozzle stem, fitting and manifold; igniter.
  sk.add('fill', poly([[719, 112], [723, 112], [723, 83], [726, 80.5], [726, 77.5], [719, 80]]));
  sk.add('fill', rect(715, 727, 112, 116));
  sk.add('line', circle(710, 115, 2.6));
  sk.add('fill', rect(752, 755, 91.5, 108));
  // Flame (the only amber in the drawing besides the progress rule).
  sk.add('flame',
    `M${pt(731, 79)}C${pt(746, 90)} ${pt(772, 86)} ${pt(787, 79.5)}` +
    `C${pt(772, 73)} ${pt(746, 68)} ${pt(731, 79)}Z`);
  return sk;
}

const tHub = (x: number) => lerp(66, 62, (x - 794) / 52);
const tTip = (x: number) => lerp(88, 92, (x - 794) / 52);

function firTree(x: number, w: number, h: number) {
  return poly([
    [x, h], [x + w, h], [x + w - 1.5, h - 2], [x + w, h - 3.5], [x + w - 2, h - 5.5],
    [x + 2, h - 5.5], [x, h - 3.5], [x + 1.5, h - 2],
  ]);
}

function hpt() {
  const sk = new Sketch();
  const span = 50 / HPT_STAGES;
  for (let k = 0; k < HPT_STAGES; k++) {
    const xv = 796 + k * span;
    const xr = xv + 12;
    const cv = 9 - k;
    const cr = 9 - k;
    // Nozzle guide vane with inner/outer platforms and cooling passages.
    bladeRow(sk, { x: xv, rIn: tHub(xv), rOut: tTip(xv), chordIn: cv, bow: 2.2 }, 0);
    sk.add('fill', rect(xv - 1, xv + cv + 1, tHub(xv) - 2, tHub(xv)) + rect(xv - 1, xv + cv + 1, tTip(xv), tTip(xv) + 2));
    sk.add('muted', line([[xv + 3, tHub(xv) + 3], [xv + 3, tTip(xv) - 3]]) + line([[xv + 6, tHub(xv) + 3], [xv + 6, tTip(xv) - 3]]));
    // Rotor blade, fir-tree root, tip shroud segment, disc.
    bladeRow(sk, { x: xr, rIn: tHub(xr), rOut: tTip(xr) - 1, chordIn: cr, lean: 0.5, bow: 2 }, 1);
    sk.add('fill', firTree(xr + 0.5, cr - 1, tHub(xr)));
    sk.add('fill', rect(xr - 2, xr + cr + 2, tTip(xr) + 0.5, tTip(xr) + 4));
    sk.add('hatch', disc({ x: xr - 3, w: cr + 6, rRim: tHub(xr) - 5.5, rBore: 36, rimH: 4, web: 0.3, bore: 0.9, boreH: 5 }));
  }
  sk.add('hatch', rect(806, 845, 34, 36));
  sk.add('hatch', wall(794, 846, tTip(794) + 4, tTip(846) + 4, 5));
  return sk;
}

const lHub = (x: number) => lerp(62, 66, (x - 858) / 114);
const lTip = (x: number) => lerp(100, 120, (x - 858) / 114);

function lpt() {
  const sk = new Sketch();
  // Transition vane from the HPT into the LPT.
  bladeRow(sk, { x: 847, rIn: 62, rOut: 95, chordIn: 10, chordOut: 11, lean: 3, bow: 2 }, 0);
  const pitch = 112 / LPT_STAGES;
  const discs: { x: number; w: number }[] = [];
  for (let k = 0; k < LPT_STAGES; k++) {
    const xv = 860 + k * pitch;
    const cv = Math.min(9, pitch * 0.4);
    const xr = xv + cv + 1.5;
    const cr = Math.min(9.5, pitch * 0.42);
    bladeRow(sk, { x: xv, rIn: lHub(xv) + 1, rOut: lTip(xv), chordIn: cv, lean: 1, bow: 1.5 }, 0);
    bladeRow(sk, { x: xr, rIn: lHub(xr), rOut: lTip(xr) - 2.5, chordIn: cr, lean: 1, bow: 1.6 }, 1);
    // Tip shroud with knife-edge seals; honeycomb land on the case.
    const t = lTip(xr);
    sk.add('fill', rect(xr - 0.5, xr + cr + 1.5, t - 2.5, t - 1));
    sk.add('line', line([[xr + 2, t - 1], [xr + 2, t + 0.8]]) + line([[xr + cr - 1, t - 1], [xr + cr - 1, t + 0.8]]));
    sk.add('muted', line([[xr - 1, t + 1.2], [xr + cr + 2, t + 1.2]]));
    discs.push({ x: xr - 1, w: cr + 2 });
  }
  discs.forEach((d, k) => {
    sk.add('hatch', disc({ x: d.x, w: d.w, rRim: lHub(d.x) - 0.5, rBore: 24, rimH: 4, web: 0.26, bore: 0.8, boreH: 4 }));
    const nx = discs[k + 1];
    if (nx) {
      const h = lHub((d.x + nx.x) / 2);
      sk.add('hatch', rect(d.x + d.w, nx.x, h - 4.5, h - 3));
    }
  });
  // Drive cone down to the LP shaft.
  const cone = discs[Math.min(2, discs.length - 1)];
  sk.add('hatch', poly([[cone.x + cone.w / 2 - 1.5, 30], [cone.x + cone.w / 2 + 1.5, 30], [958, 17], [953, 17]]));
  sk.add('hatch', wall(846, 974, 97, lTip(974), 5));
  return sk;
}

function exhaust() {
  const sk = new Sketch();
  // Turbine rear frame: outer ring, hub, leaned struts, #5 bearing support.
  sk.add('hatch', poly([[972, 120], [1002, 123], [1002, 128], [972, 125]]));
  sk.add('hatch', rect(972, 1002, 61, 66));
  bladeRow(sk, { x: 976, rIn: 66, rOut: 120.5, chordIn: 22, chordOut: 20, lean: 2, bow: 3 }, 1);
  sk.add('hatch', poly([[986, 61], [990, 61], [992, 26], [988, 26]]));
  // Core nozzle and centre-body plug.
  sk.add('hatch', poly([[1002, 123], [1070, 110], [1070, 113], [1002, 127]]));
  sk.add('fill', `M${pt(1002, 66)}C${pt(1040, 64)} ${pt(1086, 32)} ${pt(1118, 0)}L${pt(1002, 0)}Z`);
  sk.add('muted', line([[1034, 0], [1034, 58]]));
  return sk;
}

function nacelle() {
  const sk = new Sketch();
  // Fan cowl skin over the fan case.
  sk.add('fill', `M${pt(272, 226)}Q${pt(350, 230)} ${pt(442, 229)}L${pt(442, 226)}Q${pt(350, 227)} ${pt(272, 223)}Z`);
  // Thrust reverser translating sleeve, ending in the fan nozzle.
  sk.add('fill',
    `M${pt(442, 229)}C${pt(560, 229)} ${pt(690, 214)} ${pt(772, 190)}L${pt(772, 187)}` +
    `C${pt(700, 188)} ${pt(560, 193)} ${pt(442, 196)}Z`);
  // Cascade box with vanes, blocker door, sleeve joint.
  sk.add('line', rect(452, 522, 201, 219));
  let cascades = '';
  for (let k = 0; k < 9; k++) cascades += line([[456 + k * 7.5, 202], [461 + k * 7.5, 218]]);
  sk.add('muted', cascades);
  sk.add('muted', line([[534, 197], [522, 214]]));
  sk.add('line', line([[530, 195.5], [530, 226.5]]));
  return sk;
}

function coreCowl() {
  const sk = new Sketch();
  sk.add('fill',
    `M${pt(472, 120)}C${pt(560, 132)} ${pt(680, 138)} ${pt(770, 138)}` +
    `C${pt(860, 138)} ${pt(940, 134)} ${pt(1002, 130)}L${pt(1002, 127)}` +
    `C${pt(940, 131)} ${pt(860, 135)} ${pt(770, 135)}C${pt(680, 135)} ${pt(560, 129)} ${pt(476, 117)}Z`);
  sk.add('muted', line([[620, 131], [620, 136.5]]) + line([[770, 135], [770, 138]]));
  return sk;
}

function shafts() {
  const sk = new Sketch();
  // LP shaft (fan to LPT) and HP spool, hatched as sectioned tubes.
  sk.add('hatch', rect(304, 990, 12, 17));
  sk.add('hatch', rect(304, 340, 17, 20));
  sk.add('hatch', rect(514, 846, 28, 34));
  // Bearings #1-#5 and their sump housings.
  bearing(sk, 346, 356, 17, 24);
  bearing(sk, 368, 378, 17, 24);
  bearing(sk, 516, 526, 34, 41);
  bearing(sk, 848, 856, 17, 28);
  bearing(sk, 985, 993, 17, 26);
  sk.add('muted', line([[356, 24], [470, 68]]) + line([[378, 24], [472, 64]]));
  sk.add('muted', line([[526, 41], [528, 48]]));
  return sk;
}

const SHAFT_OUTLINE = rect(304, 990, 12, 17) + rect(514, 846, 28, 34);

/* ── dimensions ───────────────────────────────────────────────────────── */

function arrow(x: number, r: number, dir: 'l' | 'r' | 'u' | 'd') {
  const a = 7;
  const b = 2.4;
  switch (dir) {
    case 'l': return poly([[x, r], [x + a, r + b], [x + a, r - b]]);
    case 'r': return poly([[x, r], [x - a, r + b], [x - a, r - b]]);
    case 'u': return poly([[x, r], [x - b, r - a], [x + b, r - a]]);
    default: return poly([[x, r], [x - b, r + a], [x + b, r + a]]);
  }
}

function dimensions(): Dimension {
  const sk = new Sketch();
  const xa = 172;
  const xb = 1118;
  const rd = -254;
  sk.add('muted', line([[xa, -209], [xa, rd - 6]]) + line([[xb, -4], [xb, rd - 6]]));
  sk.add('line', line([[xa, rd], [xb, rd]]));
  sk.add('solid', arrow(xa, rd, 'l') + arrow(xb, rd, 'r'));
  const xf = 138;
  const rf = 190;
  sk.add('muted', line([[284, rf], [xf - 6, rf]]) + line([[284, -rf], [xf - 6, -rf]]));
  sk.add('line', line([[xf, rf], [xf, -rf]]));
  sk.add('solid', arrow(xf, rf, 'u') + arrow(xf, -rf, 'd'));
  const mm = (units: number) => (Math.round((units / UNITS_PER_M) * 100) * 10).toLocaleString('en-US').replace(',', ' ');
  return {
    lines: sk.parts(),
    labels: [
      { x: sx((xa + xb) / 2), y: sy(rd) - 5, text: `${mm(xb - xa)} mm` },
      { x: sx(xf) - 6, y: AXIS_Y, text: `Ø ${mm(2 * rf)}`, rotate: -90 },
    ],
  };
}

/* ── assembly ─────────────────────────────────────────────────────────── */

const BALLOON_Y = 44;

function callout(n: number, name: string, x: number, r: number): Callout {
  return { n, name, ax: sx(x), ay: sy(r), bx: sx(x), by: BALLOON_Y };
}

function buildEngine(): Engine {
  const b = booster();
  const mod = (
    id: string, i: number, dy: number, order: number, sk: Sketch, c?: Callout, top?: Sketch,
  ): EngineModule => ({ id, i, dy, order, parts: sk.parts(), topOnly: top ? top.parts() : [], callout: c });

  // Core modules first so the translucent cowls paint over them.
  const modules = [
    mod('fan', 1, 0, 1, fan(), callout(2, 'FAN', 318, 150)),
    mod('booster', 2, 0, 2, b.sk, callout(3, 'BOOSTER', 425, 101), b.top),
    mod('hpc', 3, 0, 3, hpc(), callout(4, 'HP COMPRESSOR', 615, 82)),
    mod('combustor', 4, 0, 4, combustor(), callout(5, 'COMBUSTOR', 756, 104)),
    mod('hpt', 5, 0, 5, hpt(), callout(6, 'HP TURBINE', 818, 97)),
    mod('lpt', 6, 0, 6, lpt(), callout(7, 'LP TURBINE', 915, 113)),
    mod('exhaust', 7, 0, 7, exhaust(), callout(8, 'EXHAUST', 1044, 106)),
    mod('corecowl', EXPLODE_CENTRE, 12, 4, coreCowl()),
    mod('inlet', 0, 14, 2, inlet(), callout(1, 'INLET', 200, 223)),
    mod('nacelle', EXPLODE_CENTRE, 22, 5, nacelle()),
  ];

  return { modules, shafts: shafts().parts(), shaftOutline: SHAFT_OUTLINE, dims: dimensions() };
}

export const ENGINE: Engine = buildEngine();
